import { useQuery } from '@tanstack/react-query';
import ChapterService from '../Services/chapters/ChapterService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const GET_CHAPTERS_QUERY_KEY = 'getChapters';
export const SHOW_CHAPTER_DETAILS_QUERY_KEY = 'showChapterDetails';
export const GET_CHAPTERS_BY_COURSE_QUERY_KEY = 'getChaptersByCourse';
export const GET_CHAPTERS_BY_EDUCATIONAL_LEVEL_QUERY_KEY = 'getChaptersByEducationalLevel';

export const useGetChapters = (enabled) => {
  return useQuery({
    queryKey: [GET_CHAPTERS_QUERY_KEY],
    queryFn: ChapterService.listAllChapters,
    enabled: !!enabled, // Ensure enabled is a boolean
    staleTime: 5 * 60 * 1000, // use cached data and will not trigger a refetch for 5 minutes.
    cacheTime: 10 * 60 * 1000, // the data will remain in the cache for 10 minutes
  });
};
export const useShowChapterDetails = (id) => {
  const navigate = useNavigate();
  
  return useQuery({
    queryKey: [SHOW_CHAPTER_DETAILS_QUERY_KEY, id],
    queryFn: async () => {
      try {
        return await ChapterService.showChapterDetails(id);
      } catch (error) {
        // Handle the error here in the queryFn instead of onError
        // console.log('Error caught in queryFn:', error);
        
        const status = error?.response?.status || error?.status;
        const message = error?.response?.data?.message || error?.message;
        
        if (status === 403) {
          // Check the specific error message for educational level mismatch
          if (message?.includes('different educational level')) {
            toast.warning("Vous n'avez pas accès à ce chapitre. Il appartient à un autre niveau éducatif.", {
              duration: 3000,
            });
            setTimeout(() => navigate('/app/courses'), 1500); // Small delay to show toast
          } else if (message?.includes('Educational level not set')) {
            toast.warning("Niveau éducatif non défini pour votre compte.", {
              duration: 3000,
            });
            setTimeout(() => navigate('/app/courses'), 1500);
          } else if (error.response.data?.code === 'SUBSCRIPTION_REQUIRED') {
            toast.warning("Abonnement requis pour accéder à ce contenu.", {
              duration: 3000,
            });
            setTimeout(() => navigate('/app/courses'), 1500);
          } else {
            toast.error("Accès non autorisé à ce contenu.", {
              duration: 3000,
            });
            setTimeout(() => navigate('/app/home'), 1500);
          }
        }
        
        // Re-throw the error so React Query knows it failed
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on 403 errors
      const status = error?.response?.status || error?.status;
      if (status === 403) {
        return false;
      }
      return failureCount < 2; // Retry other errors twice
    },
    // Improved caching configuration to prevent unnecessary refetches
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false, // Prevent refetch when window regains focus
    refetchOnMount: false // Use cached data when component remounts
  });
};

export const useGetChaptersByCourse = (courseId) => {
  return useQuery({
    queryKey: [GET_CHAPTERS_BY_COURSE_QUERY_KEY, courseId],
    queryFn: () => ChapterService.getChaptersByCourse(courseId),
    // staleTime: 5 * 60 * 1000,
    // cacheTime: 10 * 60 * 1000,
  });
};

export const useGetChaptersByEducationalLevel = (enabled) => {
  return useQuery({
    queryKey: [GET_CHAPTERS_BY_EDUCATIONAL_LEVEL_QUERY_KEY],
    queryFn: ChapterService.getChaptersByEducationalLevel,
    enabled: !!enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};
